import type {
  AssetSnapshotChartData,
} from "@repo/share-log-interfaces";
import type {
  AssetListResponse,
  UserAssetGroup,
  CreateAssetDto,
  UpdateAssetDto,
  AssetSnapshotChartQuery,
} from "@repo/share-log-api-client";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

interface AssetState {
  assets: UserAssetGroup[];
  workspaceTotal: number;
  chartData: AssetSnapshotChartData | null;
  status: StateStatus;
  chartStatus: StateStatus;
}

interface AssetActions {
  fetchAssets: (workspaceId: number) => Promise<void>;
  createAsset: (
    workspaceId: number,
    dto: CreateAssetDto,
  ) => Promise<void>;
  updateAsset: (
    workspaceId: number,
    assetId: number,
    dto: UpdateAssetDto,
  ) => Promise<void>;
  deleteAsset: (workspaceId: number, assetId: number) => Promise<void>;
  reorderAssets: (workspaceId: number, assetIds: number[]) => Promise<void>;
  fetchChartData: (
    workspaceId: number,
    query?: AssetSnapshotChartQuery,
  ) => Promise<void>;
  reset: () => void;
}

export type AssetStore = AssetState & AssetActions;

const initialState: AssetState = {
  assets: [],
  workspaceTotal: 0,
  chartData: null,
  status: "idle",
  chartStatus: "idle",
};

export function createAssetStore(deps: StoreDeps) {
  return createStore<AssetStore>("AssetStore", (set, get) => ({
    ...initialState,

    fetchAssets: (workspaceId) =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const result = await deps.api.asset.list(workspaceId);
          set((state) => {
            state.assets = result.users;
            state.workspaceTotal = result.workspaceTotal;
          });
        },
      ),

    createAsset: async (workspaceId, dto) => {
      await deps.api.asset.create(workspaceId, dto);
      await get().fetchAssets(workspaceId);
    },

    updateAsset: async (workspaceId, assetId, dto) => {
      await deps.api.asset.update(workspaceId, assetId, dto);
      await get().fetchAssets(workspaceId);
    },

    deleteAsset: async (workspaceId, assetId) => {
      await deps.api.asset.remove(workspaceId, assetId);
      await get().fetchAssets(workspaceId);
    },

    /**
     * 본인 자산 순서 일괄 변경.
     *
     * optimistic update — 단일 유저 그룹의 전체 자산을 reorder 한다고 가정한다.
     * 호출부(UI)는 한 그룹 내에서만 reorder 를 발생시키며, 그룹의 전체 자산 ID 를
     * 새 순서대로 넘긴다. 서버 실패 시 이전 상태로 롤백한다.
     *
     * 가정이 깨져 어떤 그룹과도 매칭되지 않으면(예: 그룹 경계를 넘은 ID 묶음)
     * optimistic 을 건너뛰고 서버 호출 성공 후 fetchAssets 로 강제 재동기화하여
     * stale 상태를 방지한다.
     */
    reorderAssets: async (workspaceId, assetIds) => {
      const prevAssets = get().assets;
      let optimisticApplied = false;

      set((state) => {
        for (const group of state.assets) {
          const groupIds = new Set(group.assets.map((a) => a.id));
          const allInGroup = assetIds.every((id) => groupIds.has(id));
          const isFullSet = assetIds.length === group.assets.length;
          if (allInGroup && isFullSet) {
            const byId = new Map(group.assets.map((a) => [a.id, a]));
            group.assets = assetIds.map((id, idx) => {
              const a = byId.get(id);
              if (!a) throw new Error("asset disappeared during reorder");
              return { ...a, sortOrder: idx };
            });
            optimisticApplied = true;
            break;
          }
        }
      });

      try {
        await deps.api.asset.reorder(workspaceId, assetIds);
        if (!optimisticApplied) {
          await get().fetchAssets(workspaceId);
        }
      } catch (error) {
        if (optimisticApplied) {
          set((state) => {
            state.assets = prevAssets;
          });
        }
        throw error;
      }
    },

    fetchChartData: (workspaceId, query?) =>
      withFetchStatus(
        (s) => set((state) => { state.chartStatus = s; }),
        deps.logger,
        async () => {
          const result = await deps.api.asset.getSnapshotChart(
            workspaceId,
            query,
          );
          set((state) => {
            state.chartData = result;
          });
        },
      ),

    reset: createReset(set as ImmerSet<AssetState>, initialState),
  }));
}
